<template>
    <DForm @submit.prevent="preview">
        <section class="mb-5">
            <h2 class="h4">
                {{ __('customers.credit_notes.credit_note_type') }}
            </h2>
            <DRow>
                <DCol col="4">
                    <DSelect
                        id="credit_note_type"
                        v-model="form.credit_note_type_id"
                        :placeholder="__('customers.credit_notes.form.select_credit_note_type')"
                        :options="creditNoteTypeOptions"
                        :is-valid="creditNoteTypeValid"
                        :invalid-feedback="creditNoteTypeError"
                        @update:value="updateCreditNoteType"
                    />
                </DCol>
            </DRow>
            <DFormRow
                :label="__('products.products.form.also_display_retired_products')"
                is-vertical
            >
                <DCol col="4">
                    <DSwitch
                        v-model="displayRetiredProducts"
                        color="success"
                        variant="3d"
                        @update:checked="handleRetiredChange"
                    />
                </DCol>
            </DFormRow>
            <DRow
                v-if="taxData.tax_rate !== undefined"
                class="mt-2"
            >
                <DCol>
                    <b>{{ __('customers.credit_notes.customer_details.tax_rate') }}</b>: {{ taxData.tax_rate }}%
                </DCol>
            </DRow>
            <DRow v-if="taxData.tax_key !== undefined">
                <DCol>
                    <b>{{ __('customers.credit_notes.customer_details.tax_key') }}</b>: {{ taxData.tax_key }}
                </DCol>
            </DRow>
            <DRow v-if="taxData.expense_account !== undefined">
                <DCol>
                    <b>{{ __('customers.credit_notes.customer_details.expense_account') }}</b>: {{ taxData.expense_account }}
                </DCol>
            </DRow>
            <DRow v-if="costCenter && isFinanceSystemNetsuite === false">
                <DCol>
                    <b>{{ __('customers.credit_notes.customer_details.cost_center') }}</b>: {{ costCenter }}
                </DCol>
            </DRow>
        </section>
        <section class="mb-5">
            <h2 class="h4">
                {{ __('customers.credit_notes.credit_note_items_section') }}
            </h2>
            <DTable
                :key="tableKey"
                class="mt-3"
                :items="creditNoteItem"
                :fields="tableFields"
                hover
                sorter
            >
                <template #product-header>
                    <span :class="{'required': isProductRequiredForFinanceSystem}">{{ __('customers.invoices.form.product') }}</span>
                </template>
                <template #quantity-header>
                    <span class="required">{{ __('customers.credit_notes.form.quantity') }}</span>
                </template>
                <template #line_item-header>
                    <span class="required">{{ __('customers.credit_notes.form.line_item') }}</span>
                </template>
                <template #description-header>
                    <span class="required">{{ __('customers.credit_notes.form.description') }}</span>
                </template>
                <template #from_date-header>
                    <span class="required">{{ __('customers.credit_notes.form.from_date') }}</span>
                </template>
                <template #to_date-header>
                    {{ __('customers.credit_notes.form.to_date') }}
                </template>
                <template #amount-header>
                    <span class="required">{{ __('customers.credit_notes.form.amount') }}</span>
                </template>
                <template #accounting_product-header>
                    <span class="required">{{ __('customers.credit_notes.form.accounting_product') }}</span>
                </template>
                <template #actions-header>
                    {{ __('customers.credit_notes.form.actions') }}
                </template>
                <template #product="{item}">
                    <td>
                        <DMultiSelect
                            :id="`product_${item.index}`"
                            :key="productSelectKey"
                            :options="getProductOptions(item.index)"
                            :multiple="false"
                            options-style="text"
                            :is-valid="isValid(item.index, 'product')"
                            :invalid-feedback="invalidFeedback(item.index, 'product')"
                            @update="(value) => updateValue(value, item.index)"
                        />
                    </td>
                </template>
                <template #quantity="{item}">
                    <td>
                        <DInput
                            :id="`quantity_${item.index}`"
                            v-model="form.creditNoteItem[item.index].quantity"
                            add-input-classes="bg-light"
                            :placeholder="__('customers.credit_notes.form.quantity')"
                            :is-valid="isValid(item.index, 'quantity')"
                            :invalid-feedback="invalidFeedback(item.index, 'quantity')"
                            type="number"
                            min="1"
                            max="2147483647"
                        />
                    </td>
                </template>
                <template #line_item="{item}">
                    <td>
                        <DInput
                            :id="`line_item_${item.index}`"
                            v-model="form.creditNoteItem[item.index].line_item"
                            add-input-classes="bg-light"
                            :placeholder="__('customers.credit_notes.form.line_item')"
                            :is-valid="isValid(item.index, 'line_item')"
                            :invalid-feedback="invalidFeedback(item.index, 'line_item')"
                            maxlength="255"
                        />
                    </td>
                </template>
                <template #description="{item}">
                    <td>
                        <DFormTextarea
                            :id="`description_${item.index}`"
                            v-model="form.creditNoteItem[item.index].description"
                            add-input-classes="bg-light"
                            :placeholder="__('customers.credit_notes.form.description')"
                            :is-valid="isValid(item.index, 'description')"
                            :invalid-feedback="invalidFeedback(item.index, 'description')"
                        />
                    </td>
                </template>
                <template #from_date="{item}">
                    <td>
                        <DDateFormatInput
                            :id="`from_date_${item.index}`"
                            v-model="form.creditNoteItem[item.index].from_date"
                            format="YYYY-MM-DD"
                            :placeholder="__('customers.credit_notes.form.from_date')"
                            :is-valid="isValid(item.index, 'from_date')"
                            :invalid-feedback="invalidFeedback(item.index, 'from_date')"
                        />
                    </td>
                </template>
                <template #to_date="{item}">
                    <td>
                        <DDateFormatInput
                            :id="`to_date_${item.index}`"
                            v-model="form.creditNoteItem[item.index].to_date"
                            format="YYYY-MM-DD"
                            :placeholder="__('customers.credit_notes.form.to_date')"
                            :is-valid="isValid(item.index, 'to_date')"
                            :invalid-feedback="invalidFeedback(item.index, 'to_date')"
                        />
                    </td>
                </template>
                <template #amount="{item}">
                    <td>
                        <DInput
                            :id="`amount_${item.index}`"
                            v-model="form.creditNoteItem[item.index].amount"
                            add-input-classes="bg-light"
                            :placeholder="__('customers.credit_notes.form.amount')"
                            :is-valid="isValid(item.index, 'amount')"
                            :invalid-feedback="invalidFeedback(item.index, 'amount')"
                            maxlength="13"
                        />
                    </td>
                </template>
                <template #accounting_product="{item}">
                    <td>
                        <DSelect
                            :id="`accounting_product_${item.index}`"
                            v-model="form.creditNoteItem[item.index].accounting_product"
                            add-input-classes="bg-light"
                            :options="accountingProducts"
                            :placeholder="__('customers.credit_notes.form.select_accounting_product')"
                            :is-valid="isValid(item.index, 'accounting_product')"
                            :invalid-feedback="invalidFeedback(item.index, 'accounting_product')"
                        />
                    </td>
                </template>
                <template #actions="{item}">
                    <td>
                        <DButton
                            mini
                            color="primary"
                            icon="plus"
                            :disabled="isInLoadingState"
                            @click="addNewRow(item.index)"
                        />
                        <DButton
                            mini
                            color="danger"
                            icon="trash-can-outline"
                            :disabled="isInLoadingState"
                            @click="deleteRow(item.index)"
                        />
                    </td>
                </template>
            </DTable>
            <DFormControls
                submit-icon="eye"
                :cancel-route="cancelRoute"
                :dirty="form.isDirty"
                :disabled="taxDataError !== null"
                :submit-message="__('messages.preview')"
                @submit="preview"
            />
        </section>
    </DForm>
</template>

<script>

import { toRaw } from 'vue'
import _ from 'lodash'

const tableFields = [
    { key: 'product', sorter: false, filter: false, _style: { width: '15%' } },
    { key: 'quantity', sorter: false, filter: false, _style: { width: '8%' } },
    { key: 'line_item', sorter: false, filter: false, _style: { width: '15%' } },
    { key: 'description', sorter: false, filter: false, _style: { width: '15%' } },
    { key: 'from_date', sorter: false, filter: false, _style: { width: '10%' } },
    { key: 'to_date', sorter: false, filter: false, _style: { width: '10%' } },
    { key: 'amount', sorter: false, filter: false, _style: { width: '10%' } },
    { key: 'accounting_product', sorter: false, filter: false, _style: { width: '10%' } },
    { key: 'actions', label: 'Actions', sorter: false, filter: false, _style: { width: '7%' } }
]
const tableFieldsWithoutAccountingProduct = [
    { key: 'product', sorter: false, filter: false, _style: { width: '15%' } },
    { key: 'quantity', sorter: false, filter: false, _style: { width: '10%' } },
    { key: 'line_item', sorter: false, filter: false, _style: { width: '17.5%' } },
    { key: 'description', sorter: false, filter: false, _style: { width: '17.5%' } },
    { key: 'from_date', sorter: false, filter: false, _style: { width: '10%' } },
    { key: 'to_date', sorter: false, filter: false, _style: { width: '10%' } },
    { key: 'amount', sorter: false, filter: false, _style: { width: '10%' } },
    { key: 'actions', label: 'Actions', sorter: false, filter: false, _style: { width: '10%' } }
]
const emptyRow = {
    index: 0,
    product: null,
    quantity: null,
    line_item: null,
    description: null,
    from_date: null,
    to_date: null,
    amount: null,
    accounting_product: null
}

export default {
    name: 'InvoiceForm',
    props: {
        errors: {
            type: Object,
            default: () => { return {} }
        },
        customer: {
            type: Object,
            default: () => { return {} }
        },
        accountingProducts: {
            type: Array,
            default: () => { return [] }
        },
        creditNoteTypes: {
            type: Array,
            default () {
                return []
            }
        },
        products: {
            type: Array,
            default: () => { return [] }
        },
        isProductRequiredForFinanceSystem: {
            type: Boolean,
            default: () => { return false }
        },
        retired: {
            type: Boolean,
            default: false,
            required: false
        },
        isFinanceSystemNetsuite: {
            type: Boolean,
            default: () => { return false }
        }
    },
    emits: ['preview'],
    data () {
        return {
            form: this.$inertia.form({
                credit_note_type_id: null,
                creditNoteItem: [Object.assign({}, emptyRow)]
            }),
            creditNoteItem: [Object.assign({}, emptyRow)],
            taxData: {},
            taxDataError: null,
            costCenter: null,
            creditNoteTypeId: null,
            tableKey: 0,
            displayRetiredProducts: false,
            productsList: this.products,
            productSelectKey: 1
        }
    },
    computed: {
        cancelRoute () {
            return this.$route('customers.customers.invoices.index', { customer: this.customer.id })
        },
        creditNoteTypeOptions () {
            return [{ label: '', value: '' }, ...this.creditNoteTypes.map((creditNote) => {
                return {
                    value: creditNote.id,
                    label: creditNote.name
                }
            })]
        },
        creditNoteTypeValid () {
            if (this.taxDataError !== null) {
                return false
            }

            return this.errors.credit_note_type_id ? false : null
        },
        creditNoteTypeError () {
            if (this.errors.credit_note_type_id) {
                return this.errors.credit_note_type_id[0]
            }

            return this.taxDataError
        },
        tableFields () {
            if (this.costCenter !== null) {
                return tableFieldsWithoutAccountingProduct
            }

            return tableFields
        },
        parsedErrors () {
            const parsedErrors = {}

            Object.keys(this.errors).forEach((field) => {
                parsedErrors[field] = this.errors[field][0]
            })

            return parsedErrors
        }
    },
    watch: {
        costCenter () {
            ++this.tableKey
        }
    },
    methods: {
        deleteRow (index) {
            this.form.creditNoteItem.splice(index, 1)
            this.form.creditNoteItem = this.resetItemIndexes()
            this.creditNoteItem = _.cloneDeep(this.form.creditNoteItem)
        },
        addNewRow (index) {
            this.form.creditNoteItem.splice(index + 1, 0, Object.assign({}, emptyRow))
            this.form.creditNoteItem = this.resetItemIndexes()
            this.creditNoteItem = _.cloneDeep(this.form.creditNoteItem)
            this.productSelectKey++
        },
        preview () {
            this.$emit('preview', this.form)
        },
        isValid (index, field) {
            if (typeof this.parsedErrors[`creditNoteItem.${index}.${field}`] !== 'undefined') {
                return false
            }

            return null
        },
        invalidFeedback (index, field) {
            if (this.isValid(index, field) === false) {
                return this.parsedErrors[`creditNoteItem.${index}.${field}`]
            }

            return null
        },
        updateCreditNoteType (value) {
            this.creditNoteTypeId = value
            this.form.credit_note_type_id = value
            this.fetchTaxRules(value)
            this.updateCostCenter(value)
        },
        fetchTaxRules (value) {
            window.axios.get(this.$route('customers.customers.credit-notes.get_tax_data', {
                customer: this.customer.id,
                credit_note_type: value
            }))
                .then(response => {
                    this.taxData = response.data
                    this.taxDataError = null
                })
                .catch((error) => {
                    this.taxData = {}
                    this.costCenter = null
                    this.taxDataError = error.response.data.error
                })
        },
        updateCostCenter (value) {
            for (const creditNoteType of this.creditNoteTypes) {
                if (Number(creditNoteType.id) === Number(value)) {
                    this.costCenter = creditNoteType.cost_center
                }
            }
        },
        /**
         * This function is needed since CoreUI has a bug with tables, basically you cannot access index in slots
         * @see https://github.com/coreui/coreui-vue/issues/237
         */
        resetItemIndexes () {
            const newItems = []
            let key = -1

            for (const item of this.form.creditNoteItem) {
                newItems.push({
                    ...toRaw(item),
                    index: ++key
                })
            }

            return newItems
        },
        updateValue (value, index) {
            let product

            this.products.forEach(item => {
                item.options.forEach(option => {
                    if (option.value === parseInt(value[0])) {
                        product = option
                    }
                })
            })

            this.form.creditNoteItem[index].line_item = product.text
            this.form.creditNoteItem[index].accounting_product = product.accounting_product
            this.form.creditNoteItem[index].product = product.value
        },
        handleRetiredChange (value) {
            this.displayRetiredProducts = value
            this.fetchProducts()
        },
        fetchProducts () {
            window.axios.get(this.$route('products.dropdown.products_with_product_group_list', {
                retired: this.displayRetiredProducts ? 1 : 0
            }))
                .then((res) => {
                    this.productsList = res.data
                })
        },
        getProductOptions (value) {
            const products = _.cloneDeep(this.productsList)
            products.forEach((item, index) => {
                item.options.forEach((optionItem, optionIndex) => {
                    if (optionItem.value === this.form.creditNoteItem[parseInt(value)].product) {
                        products[index].options[optionIndex].selected = true
                    }
                })
            })

            return products
        }
    }
}
</script>

<style scoped>
table td {
    vertical-align: top !important;
}
</style>
